import Axios from "axios";
import * as httpCall from "../../../../../../Utils/apis/apis";
import {
  GLOBAL_SEARCH,
  SEARCH_STRINGS,
  SEARCH_KEYS,
  ENTITY_DEFAULTS,
  ENTITY_ORDER,
  RECORD_FIELDS,
  RECORD_FIELD_ALIASES,
  HIGHLIGHT_FIELDS,
  DEFAULT_HIGHLIGHT_FIELDS,
  RECENT,
} from "./M_GlobalSearch.config";

export const capitalize = (value = "") =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const isSearchHotkey = (event) =>
  (event.ctrlKey || event.metaKey) &&
  !event.shiftKey &&
  !event.altKey &&
  SEARCH_KEYS.HOTKEY_LETTERS.includes(event.key);

const pick = (row, keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return row[key];
    }
  }
  return "";
};

const normalizeRoute = (route = "") => {
  if (!route) {
    return "";
  }
  return route.startsWith("/") ? route : `/${route}`;
};

const routeSegments = (value = "") =>
  value.split("?")[0].split("#")[0].replace(/^\/+/, "").toLowerCase().split("/");

export const isSamePage = (route = "") => {
  if (typeof window === "undefined" || !route) {
    return false;
  }
  return routeSegments(route)[0] === routeSegments(window.location.pathname)[0];
};

export const isSameCrm = (route = "") => {
  if (typeof window === "undefined" || !route) {
    return false;
  }
  const target = routeSegments(route);
  const current = routeSegments(window.location.pathname);
  return target[0] === current[0] && (target[1] || "") === (current[1] || "");
};

const normalizeRecord = (row = {}) => ({
  id: pick(row, RECORD_FIELD_ALIASES.id),
  heading: pick(row, RECORD_FIELD_ALIASES.heading),
  subHeading: pick(row, RECORD_FIELD_ALIASES.subHeading),
  entityType: pick(row, RECORD_FIELD_ALIASES.entityType),
  tag: pick(row, RECORD_FIELD_ALIASES.tag),
  route: normalizeRoute(pick(row, RECORD_FIELD_ALIASES.route)),
});

const isFailure = (response) =>
  Boolean(
    !response ||
      response.isAxiosError ||
      (response.status && response.status >= GLOBAL_SEARCH.HTTP_ERROR_STATUS) ||
      (response.response &&
        response.response.status >= GLOBAL_SEARCH.HTTP_ERROR_STATUS)
  );

let cancelSource;

export const cancelGlobalSearch = () => {
  if (cancelSource) {
    cancelSource.cancel(GLOBAL_SEARCH.CANCEL_MESSAGE);
    cancelSource = undefined;
  }
};

export const fetchGlobalSearchResults = async (keyword) => {
  cancelGlobalSearch();
  cancelSource = Axios.CancelToken.source();
  const payload = [
    {
      id: GLOBAL_SEARCH.RESULT_KEY,
      spName: GLOBAL_SEARCH.SP_NAME,
      parameters: { [GLOBAL_SEARCH.PARAM]: keyword },
    },
  ];
  const response = await httpCall.httpPost(
    GLOBAL_SEARCH.ENDPOINT,
    payload,
    { cancelToken: cancelSource.token },
    true
  );
  if (Axios.isCancel(response)) {
    return { cancelled: true };
  }
  if (isFailure(response)) {
    return { error: response || true };
  }
  const rows = response?.data?.[GLOBAL_SEARCH.RESULT_KEY];
  const list = Array.isArray(rows) ? rows : rows ? [rows] : [];
  return { data: list.map(normalizeRecord) };
};

const entityOrderIndex = (key) => {
  const index = ENTITY_ORDER.indexOf(key);
  return index === -1 ? ENTITY_ORDER.length : index;
};

export const buildGroups = (results = []) => {
  const order = [];
  const grouped = {};
  results.forEach((record) => {
    const key = (record.entityType || ENTITY_DEFAULTS.KEY).toLowerCase();
    if (!grouped[key]) {
      grouped[key] = {
        key,
        label: capitalize(record.entityType || ENTITY_DEFAULTS.LABEL),
        items: [],
      };
      order.push(key);
    }
    grouped[key].items.push(record);
  });
  return order
    .map((key) => grouped[key])
    .sort((a, b) => entityOrderIndex(a.key) - entityOrderIndex(b.key));
};

export const getHighlightConfig = (item = {}) => {
  const key = (item.entityType || item.iconKey || "").toLowerCase();
  const fields = HIGHLIGHT_FIELDS[key] || DEFAULT_HIGHLIGHT_FIELDS;
  return {
    heading: fields.includes(RECORD_FIELDS.HEADING),
    subHeading: fields.includes(RECORD_FIELDS.SUB_HEADING),
  };
};

export const buildTabs = (groups = []) => [
  {
    key: GLOBAL_SEARCH.ALL_TAB,
    label: SEARCH_STRINGS.ALL_TAB,
    count: groups.reduce((sum, group) => sum + group.items.length, 0),
  },
  ...groups.map((group) => ({
    key: group.key,
    label: group.label,
    count: group.items.length,
  })),
];

export const flattenGroups = (groups = []) =>
  groups.reduce((all, group) => all.concat(group.items), []);

const recentKeyOf = (item = {}) => item.route || item.id || "";

export const getRecentSearches = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    const stored = window.localStorage.getItem(RECENT.STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT.MAX) : [];
  } catch (error) {
    return [];
  }
};

export const addRecentSearch = (item) => {
  if (typeof window === "undefined" || !window.localStorage || !item) {
    return getRecentSearches();
  }
  const key = recentKeyOf(item);
  if (!key) {
    return getRecentSearches();
  }
  const entry = {
    id: item.id,
    heading: item.heading,
    subHeading: item.subHeading,
    entityType: item.entityType,
    tag: item.tag,
    route: item.route,
  };
  const next = [
    entry,
    ...getRecentSearches().filter((row) => recentKeyOf(row) !== key),
  ].slice(0, RECENT.MAX);
  try {
    window.localStorage.setItem(RECENT.STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    return getRecentSearches();
  }
  return next;
};

export const clearRecentSearches = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return [];
  }
  try {
    window.localStorage.removeItem(RECENT.STORAGE_KEY);
  } catch (error) {
    return [];
  }
  return [];
};

export const buildRecentGroup = (recent = []) =>
  recent.length
    ? [{ key: RECENT.GROUP_KEY, label: SEARCH_STRINGS.RECENT, items: recent }]
    : [];

export const splitHighlight = (text = "", query = "") => {
  const source = String(text);
  const term = String(query).trim();
  if (!term || !source) {
    return [{ text: source, isMatch: false }];
  }
  const lowerSource = source.toLowerCase();
  const lowerTerm = term.toLowerCase();
  const segments = [];
  let cursor = 0;
  let index = lowerSource.indexOf(lowerTerm);
  while (index !== -1) {
    if (index > cursor) {
      segments.push({ text: source.slice(cursor, index), isMatch: false });
    }
    segments.push({
      text: source.slice(index, index + term.length),
      isMatch: true,
    });
    cursor = index + term.length;
    index = lowerSource.indexOf(lowerTerm, cursor);
  }
  if (cursor < source.length) {
    segments.push({ text: source.slice(cursor), isMatch: false });
  }
  return segments;
};
